import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { metricService, Metric, MetricValue } from '../../services/metricService';
import './Metrics.css';

interface MetricsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const Metrics: React.FC<MetricsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [metricValues, setMetricValues] = useState<{ [key: string]: MetricValue }>({});
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [calculatingValues, setCalculatingValues] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [deletingMetrics, setDeletingMetrics] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (user?.id) {
      loadMetrics();
    }
  }, [user, selectedCategory]);

  const loadMetrics = async () => {
    if (!user?.id) return;

    try {
      setLoadingMetrics(true);
      setError(null);
      // Reset values when loading new metrics
      setMetricValues({});
      setCalculatingValues({});

      // Get all metrics filtered by category if selected
      const filters = selectedCategory !== 'all' ? { category: selectedCategory, is_active: true } : { is_active: true };
      const metricsResponse = await metricService.getAllMetrics(filters);

      if (metricsResponse.success && metricsResponse.metrics) {
        setMetrics(metricsResponse.metrics);

        // Get all categories (only on first load or if categories are empty)
        if (categories.length === 0) {
          const allCategories = await metricService.getMetricCategories();
          setCategories(allCategories);
        }

        // Stop loading metrics list
        setLoadingMetrics(false);

        // Calculate current values for all metrics in parallel
        // Show metrics list first, then calculate values
        const calculatingState: { [key: string]: boolean } = {};
        metricsResponse.metrics.forEach(metric => {
          calculatingState[metric.name] = true;
        });
        setCalculatingValues(calculatingState);

        // Calculate all metrics in parallel
        const valuePromises = metricsResponse.metrics.map(async (metric) => {
          try {
            const valueResponse = await metricService.calculateMetric(metric.name, user.id);
            if (valueResponse.success && valueResponse.metric) {
              // Update individual metric value as it completes
              setMetricValues(prev => ({ ...prev, [metric.name]: valueResponse.metric! }));
              setCalculatingValues(prev => ({ ...prev, [metric.name]: false }));
            } else {
              setCalculatingValues(prev => ({ ...prev, [metric.name]: false }));
            }
          } catch (err) {
            console.error(`Error calculating metric ${metric.name}:`, err);
            setCalculatingValues(prev => ({ ...prev, [metric.name]: false }));
          }
        });

        // Wait for all calculations to complete
        await Promise.all(valuePromises);
      } else {
        setError(metricsResponse.message || 'Failed to load metrics');
        setLoadingMetrics(false);
      }
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError('Failed to load metrics. Please try again.');
      setLoadingMetrics(false);
    }
  };

  const handleDeleteMetric = async (metricName: string) => {
    if (!window.confirm(`Are you sure you want to delete the metric "${metricName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingMetrics(prev => ({ ...prev, [metricName]: true }));
      setError(null);

      const response = await metricService.deleteMetric(metricName);
      
      if (response.success) {
        await loadMetrics();
      } else {
        setError(response.message || 'Failed to delete metric');
      }
    } catch (err) {
      console.error('Error deleting metric:', err);
      setError('Failed to delete metric. Please try again.');
    } finally {
      setDeletingMetrics(prev => ({ ...prev, [metricName]: false }));
    }
  };

  const groupedMetrics = metricService.groupMetricsByCategory(metrics);

  return (
    <div className="metrics-page">
      <header className="metrics-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
        </div>
        <h1>Metrics</h1>
        <div className="header-right">
          <button className="metrics-builder-btn" onClick={() => navigate('/metrics/builder')}>
            Build Metric
          </button>
        </div>
      </header>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="error-close-btn">×</button>
        </div>
      )}

      <div className="metrics-content">
        {loadingMetrics ? (
          <div className="loading-state">
            <p>Loading metrics...</p>
          </div>
        ) : (
          <>
            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="metrics-filter">
                <label>Filter by category:</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Metrics List Grouped by Category */}
            {Object.entries(groupedMetrics).map(([category, categoryMetrics]) => (
              <div key={category} className="metrics-category-section">
                <h2 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h2>
                <div className="metrics-list">
                  {categoryMetrics.map((metric) => {
                    const metricValue = metricValues[metric.name];
                    const isCalculating = calculatingValues[metric.name];
                    return (
                      <div 
                        key={metric.id} 
                        className="metric-card"
                        onClick={() => navigate(`/metrics/${encodeURIComponent(metric.name)}`)}
                      >
                        <div className="metric-header">
                          <h3 className="metric-name">{metric.name}</h3>
                          <div className="metric-header-right">
                            <span className="metric-category-badge">{metric.category}</span>
                            <button
                              className="metric-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMetric(metric.name);
                              }}
                              disabled={deletingMetrics[metric.name]}
                              title="Delete metric"
                            >
                              {deletingMetrics[metric.name] ? '...' : '×'}
                            </button>
                          </div>
                        </div>
                        {metric.description && (
                          <p className="metric-description">{metric.description}</p>
                        )}
                        <div className="metric-value-section">
                          {isCalculating ? (
                            <div className="metric-value-loading">
                              <span className="loading-spinner-small"></span>
                              Calculating...
                            </div>
                          ) : metricValue ? (
                            <>
                              <div className="metric-value">
                                {metricService.formatMetricValue(metricValue.value, metricValue.unit)}
                              </div>
                              <div className="metric-unit">{metric.unit}</div>
                            </>
                          ) : (
                            <div className="metric-value-error">Unable to calculate</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {metrics.length === 0 && (
              <div className="empty-state">
                <p>No metrics available.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Metrics;

